import Image from "next/image";
import Link from "next/link";

/**
 * In-content VTCT course cards for Batch 16 Item 3.3.
 * One link per course (card title). Hrefs are string literals so
 * scripts/check-internal-links.ts can attribute mounts to head-term owners.
 * Pathway and quiz sit as CTAs below the grid (still counted as mounts).
 */

type CourseCard = {
  href: string;
  title: string;
  blurb: string;
  imageUrl: string | null;
};

type CourseGroup = {
  heading: string;
  items: CourseCard[];
};

const COURSE_GROUPS: CourseGroup[] = [
  {
    heading: "Skin and laser",
    items: [
      {
        href: "/courses/vtct/level-4-in-skin-microneedling/",
        title: "VTCT Level 4 Microneedling",
        blurb: "Build practical skin needling skills, from consultation and needle selection to treatment planning and aftercare.",
        imageUrl:
          "https://s3.eu-west-2.amazonaws.com/assets.cosmetic.college/cms/4hVp7FxJsG6xysRbFnhYFN/shutterstock_2140727647.jpg",
      },
      {
        href: "/courses/vtct/level-4-award-in-skin-peeling/",
        title: "VTCT Level 4 Chemical Peels",
        blurb: "Select and apply superficial chemical peels, with skin analysis, practical training and tailored aftercare.",
        imageUrl:
          "https://s3.eu-west-2.amazonaws.com/assets.cosmetic.college/cms/2hfsgOcl06FkvsmVJw4CZb/CHEMICAL_SKIN_PEEL.jpg",
      },
      {
        href: "/courses/vtct/level-4-award-in-dermaplaning/",
        title: "VTCT Level 4 Dermaplaning",
        blurb: "Practise skin preparation and precise blade techniques, with client suitability checks and dermaplaning aftercare.",
        imageUrl:
          "https://s3.eu-west-2.amazonaws.com/assets.cosmetic.college/cms/4sQUUdOQljK83lkQccsB2Z/dermaplaning_pic_.jpg",
      },
      {
        href: "/courses/vtct/level-4-award-in-high-intensity-focused-ultrasound-hifu/",
        title: "VTCT Level 4 HIFU",
        blurb: "Learn focused ultrasound treatment protocols, from client consultation to treatment planning and home care advice.",
        imageUrl:
          "https://s3.eu-west-2.amazonaws.com/assets.cosmetic.college/cms/4RPe2plehIpiFOS1liCVRR/Cosmetic_college_hifu_training.png",
      },
      {
        href: "/courses/vtct/vtct-level-4-plasma-pen-training/",
        title: "VTCT Level 4 Plasma Pen",
        blurb: "Develop plasma pen and fibroblast techniques through practical case studies, covering client suitability, safety and aftercare.",
        imageUrl:
          "https://s3.eu-west-2.amazonaws.com/assets.cosmetic.college/cms/3bfWR9RwOYh34ZTpjmrZy/facial_blast.jpg",
      },
      {
        href: "/courses/vtct/level-4-radio-frequency-and-ultrasound/",
        title: "VTCT Level 4 Radio Frequency and Ultrasound",
        blurb: "Train with radio frequency and ultrasound devices, gaining two Level 4 certificates on one combined course.",
        imageUrl:
          "https://s3.eu-west-2.amazonaws.com/assets.cosmetic.college/cms/2TKMxgDrntTf4nBYmFKw6E/VTCT_Level_4_Radiofrequency___Ultrasound_Training_At_The_Cosmetic_College.png",
      },
      {
        href: "/courses/vtct/level-6-award-in-mesotherapy-treatments/",
        title: "VTCT Level 6 Mesotherapy",
        blurb: "Build on your skin needling experience with mesotherapy for skin rejuvenation, combining online theory and practice on live models.",
        imageUrl:
          "https://s3.eu-west-2.amazonaws.com/assets.cosmetic.college/cms/77bydUFFYitmohlij10t3X/shutterstock_1407635186.jpg",
      },
      {
        href: "/courses/vtct/laser-hair-removal/",
        title: "VTCT Level 4 Laser Hair Removal",
        blurb: "Train in IPL, Nd:YAG and diode laser hair removal to broaden your clinic’s treatment offering.",
        imageUrl:
          "https://s3.eu-west-2.amazonaws.com/assets.cosmetic.college/cms/4Fd21vgbsimRH63rXx4ctc/shutterstock_435284326.jpg",
      },
      {
        href: "/courses/vtct/level-5-certificate-in-laser-tattoo-removal/",
        title: "VTCT Level 5 Laser Tattoo Removal",
        blurb: "Extend your Level 4 laser skills into tattoo removal, with laser theory and practical treatments on live models.",
        imageUrl:
          "https://s3.eu-west-2.amazonaws.com/assets.cosmetic.college/cms/7D20Uc0mfHyhTVo3tGIvdA/laser-tattoo-removal-cosmetic-college.jpg",
      },
      {
        href: "/courses/vtct/level-4-certificate-in-advanced-aesthetic-therapies-for-skin/",
        title: "VTCT Level 4 Advanced Aesthetic Therapies for Skin",
        blurb: "Study microneedling and superficial chemical peels together, building a skin rejuvenation foundation for progression to Level 5.",
        imageUrl:
          "https://s3.eu-west-2.amazonaws.com/assets.cosmetic.college/cms/6k2hsxx01vnC0lCOPlTJb/Untitled_design__14_.png",
      },
    ],
  },
  {
    heading: "Facials, permanent makeup and injectables",
    items: [
      {
        href: "/courses/vtct/level-2-facial-and-skincare-course/",
        title: "VTCT Level 2 Facial and Skincare",
        blurb: "Start your facial training with skin analysis, cleansing, exfoliation, massage and masks for professional skincare treatments.",
        imageUrl:
          "https://s3.eu-west-2.amazonaws.com/assets.cosmetic.college/cms/3BTAPcoo9vGqkppjahUgSG/Untitled_design__4_.png",
      },
      {
        href: "/courses/vtct/level-4-certificate-in-micropigmentation/",
        title: "VTCT Level 4 Permanent Makeup",
        blurb: "Learn machine techniques for ombré, hairstroke and combination brows, plus eyeliner, lip blush and lip liner.",
        imageUrl:
          "https://s3.eu-west-2.amazonaws.com/assets.cosmetic.college/cms/55dzmvNLOk5pkT5gbLAAat/4.png",
      },
      {
        href: "/courses/vtct/level-4-certificate-in-enhancing-eyebrows-with-microblading-techniques/",
        title: "VTCT Level 4 Microblading",
        blurb: "Create manual hairstroke brows, with practical training in brow mapping, shaping, colour theory and aftercare.",
        imageUrl:
          "https://s3.eu-west-2.amazonaws.com/assets.cosmetic.college/cms/7eihTWsHeY8qorsyy0ZW8M/2.png",
      },
      {
        href: "/courses/vtct/level-7-diploma-in-non-surgical-aesthetic-injectable-procedures/",
        title: "VTCT Level 7 Aesthetics Diploma",
        blurb: "Develop skills in anti-wrinkle injections, dermal fillers and skin boosters, alongside consultation, clinical assessment and complication management.",
        imageUrl:
          "https://s3.eu-west-2.amazonaws.com/assets.cosmetic.college/cms/CJiCvB4ASA77YdnbUAP42/Youtube_Landscape.00_01_01_01.Still005__1_.jpg",
      },
    ],
  },
];

/** Keep these as string literals for the internal-link checker. */
const QUIZ_HREF = "/which-aesthetics-course-is-right-for-me/";
const PATHWAY_HREF = "/pathway-to-aesthetics/";

type HeadTermLinksProps = {
  title?: string;
  /** Unique variant so the intro line differs slightly across pages. */
  variant?: "hub" | "guide" | "planning";
};

function CourseTile({ course }: { course: CourseCard }) {
  return (
    <Link
      href={course.href}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition hover:border-brand-300 hover:shadow-md"
    >
      <div className="relative aspect-[16/9] w-full shrink-0 overflow-hidden bg-gradient-to-br from-brand-50 via-white to-gray-100">
        {course.imageUrl ? (
          <Image
            src={course.imageUrl}
            alt={course.title}
            fill
            sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 280px"
            className="object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div
            className="absolute inset-0 flex items-center justify-center px-3"
            aria-hidden="true"
          >
            <span className="rounded-full border border-brand-100 bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-brand-700">
              VTCT
            </span>
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col p-3.5 sm:p-4">
        <h4 className="text-sm font-semibold leading-snug text-gray-900 group-hover:text-brand-700 sm:text-base">
          {course.title}
        </h4>
        <p className="mt-1.5 text-xs leading-5 text-gray-600 sm:text-sm">
          {course.blurb}
        </p>
      </div>
    </Link>
  );
}

export default function HeadTermLinks({ variant = "hub", title }: HeadTermLinksProps) {
  const intro =
    variant === "guide"
      ? "Related VTCT courses to explore next"
      : variant === "planning"
        ? "Compare these regulated VTCT courses when you plan your route"
        : "Browse our main VTCT courses by treatment";

  return (
    <section
      aria-label="Main VTCT course pages"
      className="not-prose my-8 rounded-3xl border border-gray-200 bg-white p-5 shadow-sm shadow-gray-100 sm:p-8"
    >
      <h2 className="text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">
        {title ?? intro}
      </h2>
      <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-600 sm:text-base">
        Regulated VTCT qualifications at our Orpington training centre, near
        London.
      </p>

      <div className="mt-8 space-y-8">
        {COURSE_GROUPS.map((group) => (
          <div key={group.heading}>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              {group.heading}
            </h3>
            <ul className="mt-3 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4">
              {group.items.map((course) => (
                <li key={course.href} className="min-w-0">
                  <CourseTile course={course} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="mt-8 rounded-2xl border border-brand-100 bg-brand-50/50 p-4 sm:p-5">
        <p className="text-sm font-semibold text-gray-900 sm:text-base">
          Not sure where to start?
        </p>
        <p className="mt-1 text-sm leading-6 text-gray-600">
          Use the course selector or see how the Pathway to Aesthetics builds
          from beginner to advanced.
        </p>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
          <Link
            href={QUIZ_HREF}
            className="inline-flex items-center justify-center rounded-xl bg-brand-600 px-5 py-3 text-sm font-semibold text-white shadow-md shadow-brand-200 transition hover:bg-brand-700"
          >
            Not sure which course? Take the course selector
          </Link>
          <Link
            href={PATHWAY_HREF}
            className="inline-flex items-center justify-center rounded-xl border border-gray-200 bg-white px-5 py-3 text-sm font-semibold text-gray-700 shadow-sm transition hover:border-brand-300"
          >
            See the Pathway to Aesthetics
          </Link>
        </div>
      </div>
    </section>
  );
}
